import React from 'react'
import styled from 'styled-components'
import { colors, colorSystem } from '@/styles/theme'
import { tokens, colors as layerColors } from '@musinsa/layer-tokens/desktop'
import Text from '@/components/content/Text'

export type BadgeType = 'ACTIVE' | 'PENDING' | 'ENDED' | 'CUSTOM'

export interface BadgeProps {
  type: BadgeType
  backgroundColor?: string
  textColor?: string
  text?: string
  className?: string
  style?: React.CSSProperties
}

// 타입별 텍스트와 색상 매핑
const badgeConfig = {
  ACTIVE: {
    text: '진행중',
    backgroundColor: '#F0F5FF',
    textColor: tokens.layerDesktop['fg-accent'],
  },
  PENDING: {
    text: '대기',
    backgroundColor: layerColors['bg-success-low'],
    textColor: layerColors['fg-success'],
  },
  ENDED: {
    text: '종료',
    backgroundColor: colors.gray100,
    textColor: colorSystem.text.common.disabled_inverse,
  },
} as const

/**
 * MEMO: 새로운 타입이 필요한 경우
 * 1. BadgeType에 새로운 타입 추가 (예: 'error' | 'success')
 * 2. badgeConfig에 해당 타입의 설정 추가
 *    예: error: { text: '에러', backgroundColor: colors.red100, textColor: colors.red500 }
 *
 * 특수한 경우에는 custom 타입 사용을 권장합니다. (예: 색상을 달리해야할 경우 등)
 *
 * 사용법:
 * - 기본 사용: <Badge type="progress" />
 * - 커스텀 사용: <Badge type="custom" text="에러" backgroundColor="#FEE4E4" textColor="#F94A4A" />
 */
function Badge({ type, backgroundColor, textColor, text, className, style }: BadgeProps) {
  const config =
    type === 'CUSTOM'
      ? {
          text: text || '',
          backgroundColor: backgroundColor || colors.gray100,
          textColor: textColor || colors.gray600,
        }
      : badgeConfig[type]

  return (
    <BadgeContainer $backgroundColor={config.backgroundColor} $textColor={config.textColor} className={className} style={style}>
      <StyledText kind="Body_12_regular" color={config.textColor}>
        {config.text}
      </StyledText>
    </BadgeContainer>
  )
}

const BadgeContainer = styled.span<{
  $backgroundColor: string
  $textColor: string
}>`
  border-radius: 4px;
  white-space: nowrap;
  padding: 0px 8px;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  color: ${({ $textColor }) => $textColor};
`

const StyledText = styled(Text)`
  padding-top: 3px; // 라인 높이 맞추기 위해 추가
`

export default Badge
